/**
 * 左の項目を「できることの束」で出し分ける（画面に出す・出さないの計算だけ）。
 *
 * **判定の正はサーバ側の門番**（server/src/auth/access.ts）。
 * ここで出し分けるのは見せ方の話で、ここを通り抜けても、サーバが許さない操作はできない。
 * 逆に、ここで出していない項目でも、サーバが許していれば権限としては持っている。
 */
import { type MenuItem, type MenuSection } from './items.js';

export interface Grant {
  /** できることの束（サーバの `GET /org/roles` が返す値） */
  capabilities: Record<string, boolean>;
  /** 受け持ちの範囲 */
  scope: 'org' | 'site';
  role?: string;
  /**
   * 「管理の仕事」にあたるできることの一覧。
   * **サーバが返すもの（`GET /org/roles` の `management_capabilities`）をそのまま入れる**。
   * 画面側で作らない（issue #159）。
   */
  managementCapabilities: readonly string[];
  /** このサーバに「認証番号の控え」があるか（検証のサーバだけ・issue #342）。無ければ false 扱い */
  verificationSmsOutbox?: boolean;
}

/** その「できること」を持っているか。 */
export function can(grant: Grant, capability: string): boolean {
  return grant.capabilities[capability] === true;
}

/** 管理の仕事を1つでも持っているか（＝管理画面を使う方か）。 */
export function isAdminGrant(grant: Grant): boolean {
  return grant.managementCapabilities.some((key) => can(grant, key));
}

/** 左の項目1つを出してよいか。 */
export function isMenuItemVisible(item: MenuItem, grant: Grant): boolean {
  if (item.hqOnly && grant.role !== 'hq_admin') return false;
  if (item.orgScopeOnly && grant.scope !== 'org') return false;
  if (item.verificationOnly && grant.verificationSmsOutbox !== true) return false;
  if (item.requires === 'admin') return isAdminGrant(grant);
  return can(grant, item.requires);
}

/** 見える項目だけを残す（項目が1つも残らない分類は落とす）。 */
export function visibleSections(sections: readonly MenuSection[], grant: Grant): MenuSection[] {
  return sections
    .map((section) => ({
      ...(section.title !== undefined ? { title: section.title } : {}),
      items: section.items.filter((item) => isMenuItemVisible(item, grant)),
    }))
    .filter((section) => section.items.length > 0);
}
